/**
 * Table turnover helpers — power the live floor view (a running stopwatch +
 * color escalation on each occupied table in Admin → Table Management).
 *
 * "Turnover" = how long a dine-in party occupies a table: from when they were
 * seated / the tab was opened (`table_opened_at`, falling back to `created_at`
 * for orders rung up without an open tab) until the check is fully paid.
 *
 * Thresholds are deliberately plain constants — tune here and both the live
 * tiles and any future settings UI stay in agreement.
 */

/** Below this many minutes a table is "fresh" (green). */
export const TURNOVER_WARN_MINUTES = 60
/** At/above this many minutes a table is "over" (red) — a likely camper. */
export const TURNOVER_OVER_MINUTES = 120

/**
 * A seated-empty tab (open tab, zero items) flagged as needing attention after
 * this long — either the party is slow to order or they left and the tab was
 * never released. The backend sweeper auto-releases such tabs after the
 * tables.empty_tab_auto_release_hours setting (default 1h); this is the
 * earlier on-floor visual escalation.
 */
export const SEATED_EMPTY_STALE_MINUTES = 30

/**
 * Seated but nothing ordered yet: an open tab with zero non-voided items.
 * Distinguishes "party just sat down / abandoned tab" from a table with a
 * real order on the floor map.
 */
export function isSeatedEmpty(
  order?: { is_open_tab?: boolean; item_count?: number } | null,
): boolean {
  if (!order) return false
  // Zero non-voided items is authoritative: a dine-in order whose every line
  // was voided is "seated · no order" even though it was already fired (which
  // clears is_open_tab). Fall back to is_open_tab only when item_count is
  // absent from the payload.
  if (typeof order.item_count === 'number') return order.item_count === 0
  return !!order.is_open_tab
}

export type TurnoverStatus = 'fresh' | 'warn' | 'over'

export function turnoverStatus(minutes: number): TurnoverStatus {
  if (minutes >= TURNOVER_OVER_MINUTES) return 'over'
  if (minutes >= TURNOVER_WARN_MINUTES) return 'warn'
  return 'fresh'
}

/** Minutes elapsed between an ISO start and `now` (ms epoch). Never negative. */
export function minutesSince(startIso: string, now: number): number {
  const start = new Date(startIso).getTime()
  if (Number.isNaN(start)) return 0
  return Math.max(0, (now - start) / 60000)
}

/** Compact duration label: "<1m" / "45m" / "1h 20m" / "3h". A positive but
 *  sub-minute value reads "<1m" rather than "0m" so an order that was rung up
 *  and paid in seconds doesn't masquerade as a literal zero. */
export function formatDuration(minutes: number): string {
  if (minutes <= 0) return '0m'
  if (minutes < 1) return '<1m'
  const total = Math.round(minutes)
  if (total < 60) return `${total}m`
  const h = Math.floor(total / 60)
  const m = total % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

/** Tailwind classes per status for the live tile accent, label and dot. */
export const TURNOVER_STATUS_STYLES: Record<
  TurnoverStatus,
  { border: string; text: string; dot: string }
> = {
  fresh: { border: 'border-l-emerald-400', text: 'text-success-fg', dot: 'bg-emerald-500' },
  warn: { border: 'border-l-amber-400', text: 'text-warning-fg', dot: 'bg-amber-500' },
  over: { border: 'border-l-red-500', text: 'text-danger-fg', dot: 'bg-red-500' },
}
